import React, { useState, useEffect } from "react";

import DB from "_DatabaseFunctions/directConnect";
import { Button, Box, Typography, FormControlLabel, Checkbox } from "@mui/material";

import ModalComp from "app/Components/Modals/ModalTemplate";

interface GameInfo {
  id: string;
  hostId: string;
  gameName: string;
  minPlayers: number;
  maxPlayers: number;
  gameSessionId?: string;
  waitlist: { [key: string]: any };
}

interface ModalDets {
  open: boolean;
  status: string;
}

interface ManageGameModalProps {
  dets: ModalDets;
  gameInfo: GameInfo;
}

function ManageGameModal({ dets, gameInfo }: ManageGameModalProps) {
  const [open, setShow] = useState<boolean>(false);
  const [autoAddPlayers, setAutoAddPlayers] = useState<boolean>(true);
  const [modalData, setModalData] = useState<{
    title?: string;
    msg?: string;
    type?: string;
    action?: string;
    waitlistCount?: number;
    playersToAdd?: number;
  }>({});

  // Function to automatically add waitlist players to the game
  const addWaitlistPlayersToGame = async () => {
    if (!autoAddPlayers) return;

    const waitlistPlayers = Object.keys(gameInfo.waitlist);
    const maxPlayersToAdd = Math.min(waitlistPlayers.length, gameInfo.maxPlayers);
    
    // Sort players by timestamp (first come, first served)
    const sortedPlayers = waitlistPlayers
      .map(playerId => ({
        id: playerId,
        data: gameInfo.playerlist[playerId] // Get actual player data from playerlist, not waitlist
      }))
      .filter(player => player.data) // Filter out any players without data
      .sort((a, b) => (a.data.timeStamp || 0) - (b.data.timeStamp || 0))
      .slice(0, maxPlayersToAdd);

    console.log(`Adding ${sortedPlayers.length} players to the game automatically`);

    // Add each player to the current game and remove from waitlist
    for (const player of sortedPlayers) {
      try {
        // Validate player data before processing
        if (!player.data || !player.data.id) {
          console.error(`Invalid player data for player ${player.id}:`, player.data);
          continue;
        }
        
        // Add player to current game with extended timestamp (e.g., 10 minutes)
        const expTime = Date.now() + (10 * 60 * 1000); // 10 minutes to confirm
        await DB.addPlayerToGame(gameInfo.id, player.data, expTime);
        
        // Remove player from waitlist
        await DB.removeFromWaitlist(gameInfo.id, player.id);
        
        console.log(`Added ${player.data.displayName} to game and removed from waitlist`);
      } catch (error) {
        console.error(`Error adding player ${player.id} to game:`, error);
      }
    }
  };

  const StatusMap: Record<
    string,
    { status: string; cb: () => void | Promise<void> }
  > = {
    startGame: {
      status: "Running",
      async cb() {
        const { sessionId, startedAt } = await DB.startGame(gameInfo.id);
        const { hostId, id, gameName } = gameInfo;

        await DB.startGameSession({
          gameId: id,
          hostId,
          gameName,
          startedAt,
          gameSessionId: sessionId,
        });
        
        // Automatically add waitlist players to game if option is enabled
        await addWaitlistPlayersToGame();
      },
    },
    delayGame: {
      status: "Delayed",
      cb() {},
    },
    endGame: {
      status: "Interest",
      async cb() {
        console.log(`gameInfo`, gameInfo);
        await DB.endGameSession({
          gameId: gameInfo.id,
          gameSessionId: gameInfo.gameSessionId,
        });
        await DB.endGame(gameInfo.id);
      },
    },
  };

  useEffect(() => {
    setShow(dets.open);
    if (dets.status && dets.status.trim() !== "") {
      renderModalInfo(dets.status);
    }
  }, [dets]);

  const renderModalInfo = (status: string) => {
    switch (status) {
      case "error":
        setModalData({
          title: "Error Starting Game",
          msg: `You need at least ${gameInfo.minPlayers} to start this game`,
          type: "error",
        });
        break;
      case "startGame":
        const waitlistCount = Object.keys(gameInfo.waitlist).length;
        const playersToAdd = Math.min(waitlistCount, gameInfo.maxPlayers);
        
        setModalData({
          title: "Start Game",
          msg: `Start Game ${gameInfo.gameName}?`,
          type: "info",
          action: "started",
          waitlistCount,
          playersToAdd,
        });
        break;
      case "endGame":
        setModalData({
          title: "End Game",
          msg: `End Game ${gameInfo.gameName}? \n This will delete all pending requests and remove all players from the waitlist and game`,
          type: "info",
          action: "ended",
        });
        break;
      case "delayGame":
        setModalData({
          title: "Delay Game",
          msg: `Delay Game ${gameInfo.gameName}?`,
          type: "info",
          action: "delayed",
        });
        break;
      default:
        console.warn(`ManageGameModal: Unhandled status "${status}"`);
        setModalData({
          title: "Unknown Action",
          msg: "An unknown action was requested. Please try again.",
          type: "error",
          action: "unknown",
        });
        break;
    }
  };

  const changeGameStatus = () => {
    StatusMap[dets.status].cb();
    const { status } = StatusMap[dets.status];
    DB.changeGameStatus(gameInfo.id, status);
  };

  const modalChildren = () => {
    // Special content for start game modal
    if (dets.status === "startGame" && modalData.type === "info") {
      return (
        <Box sx={{ textAlign: "center" }}>
          {modalData.waitlistCount > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Waitlist Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                There are <strong>{modalData.waitlistCount}</strong> players on the waitlist.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoAddPlayers}
                    onChange={(e) => setAutoAddPlayers(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Automatically add the first <strong>{modalData.playersToAdd}</strong> players 
                    from waitlist to the game (up to max {gameInfo.maxPlayers} players)
                  </Typography>
                }
              />
              
              {autoAddPlayers && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Players will be added in order of when they joined the waitlist and given 10 minutes to confirm their seat.
                </Typography>
              )}
            </Box>
          )}
          
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                changeGameStatus();
                setModalData({
                  type: "success",
                  title: "Success",
                  msg: `Game ${modalData.action} Successfully${autoAddPlayers && modalData.waitlistCount > 0 ? ` and ${modalData.playersToAdd} players added from waitlist` : ""}`,
                });
              }}
            >
              Start Game
            </Button>
            
            <Button variant="outlined" color="inherit" onClick={() => setShow(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      );
    }

    // Default modal content for other cases
    return (
      <div>
        {modalData.type === "info" ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              changeGameStatus();
              setModalData({
                type: "success",
                title: "Success",
                msg: `Game ${modalData.action} Successfully`,
              });
            }}
          >
            Confirm
          </Button>
        ) : (
          <span />
        )}

        <Button variant="outlined" color="inherit" onClick={() => setShow(false)}>
          Close
        </Button>
      </div>
    );
  };

  return (
    <ModalComp
      title={modalData.title}
      modalType={modalData.type}
      msg={modalData.msg}
      show={open}
      onClose={() => setShow(false)}
      action={modalData.action}
    >
      {modalChildren()}
    </ModalComp>
  );
}

export default ManageGameModal;
